import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/template/Header';
import Footer from './components/template/Footer';
import Dashboard from './components/Dashboard';
import Incidents from './components/Incidents';
import Systems from './components/Systems';
import KPI from './components/KPI';
import SeasonFx from './components/SeasonFx';
import PrivacyNotice from './components/PrivacyNotice';
import {
  ANT_SEASON_TOKENS,
  resolveSeasonFromFrontendEnv,
} from './components/seasonConfig';
import { Layout, ConfigProvider, theme } from 'antd';
import axios from './components/axiosConfig';
import './components/style.css';
import './components/responsive.css';
import './components/winter.css';
import './components/seasons.css';

const { Content } = Layout;

// Gantt library is heavy – load the projects module only when it is opened
const ProjectsPage = React.lazy(() => import('./components/projects/ProjectsPage'));

const SEASON_CLASSES = ['summer-mode', 'autumn-mode', 'spring-mode', 'winter-mode'];

const AppContent = ({ loggedInUser, setLoggedInUser, season }) => {
  const location = useLocation();
  const isProjects = ['/projects', '/systems', '/kpi'].some(p => location.pathname.startsWith(p));

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {season && <SeasonFx season={season} />}
      <Header loggedInUser={loggedInUser} setLoggedInUser={setLoggedInUser} />
      <Content className={isProjects ? 'full-content' : 'centered-content'}>
        <Routes>
          <Route path="/" element={<Incidents loggedInUser={loggedInUser} />} />
          <Route path="/dashboard" element={<Dashboard loggedInUser={loggedInUser} />} />
          <Route path="/projects" element={<React.Suspense fallback={null}><ProjectsPage loggedInUser={loggedInUser} /></React.Suspense>} />
          <Route path="/projects/:id" element={<React.Suspense fallback={null}><ProjectsPage loggedInUser={loggedInUser} /></React.Suspense>} />
          <Route path="/systems" element={<Systems loggedInUser={loggedInUser} />} />
          <Route path="/kpi" element={<KPI loggedInUser={loggedInUser} />} />
          <Route path="/incident/:id" element={<Incidents loggedInUser={loggedInUser} />} />
        </Routes>
      </Content>
      <Footer />
      <PrivacyNotice />
    </Layout>
  );
};

const App = () => {
  const [loggedInUser, setLoggedInUser] = React.useState(null);
  const [season, setSeason] = React.useState(() => resolveSeasonFromFrontendEnv());

  React.useEffect(() => {
    SEASON_CLASSES.forEach((c) => document.body.classList.remove(c));
    if (season) document.body.classList.add(`${season}-mode`);
    return () => {
      SEASON_CLASSES.forEach((c) => document.body.classList.remove(c));
    };
  }, [season]);

  React.useEffect(() => {
    axios.get('/auth/user')
      .then((response) => {
        if (response.data) setLoggedInUser(response.data);
      })
      .catch((error) => console.error('Failed to fetch user:', error));
  }, []);

  React.useEffect(() => {
    axios.get('/app-config')
      .then((r) => {
        if (r.data?.season) {
          setSeason(r.data.season);
        } else if (r.data?.winter === false) {
          // Backend season flags present but all off → default theme
          setSeason(null);
        }
        // winter/season null → keep FE REACT_APP_* initial value
      })
      .catch(() => {});
  }, []);

  const tokens = ANT_SEASON_TOKENS[season] || ANT_SEASON_TOKENS.default;

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.compactAlgorithm,
        token: tokens,
      }}
    >
      <Router>
        <AppContent
          loggedInUser={loggedInUser}
          setLoggedInUser={setLoggedInUser}
          season={season}
        />
      </Router>
    </ConfigProvider>
  );
};

export default App;
